/**
 * @module store/modules/apieditor/actions
 * @description API Editor Vuex actions for managing API configurations and mappings.
 */

import { ActionTree } from "vuex";
import { graph } from "@/store/api";
import { RootState } from "@/store/types";

import { gql } from "@apollo/client/core";
import _ from "lodash";
import { ApiEditorState } from "./types";

const actions: ActionTree<ApiEditorState, RootState> = {
  /**
   * Fetches all API configurations from the backend and commits to store.
   *
   * @param {Object} context - Vuex action context
   * @param {Object} context.state - Current module state
   * @param {Function} context.commit - Vuex commit function
   * @returns {Promise<Object>} API list as key-value object
   * @throws {Error} When GraphQL query fails
   *
   * @example
   * await store.dispatch('FETCH_ALL_APIS');
   */
  FETCH_ALL_APIS: ({ state, commit }) => {
    return graph
      .query({
        query: gql`
          query {
            Bot {
              apiSettings
            }
          }
        `,
        fetchPolicy: "no-cache",
      })
      .then((payload) => {
        const data: any = _.cloneDeep(payload.data);
        const apiListObject: any = {};
        if (Array.isArray(data.Bot.apiSettings) && data.Bot.apiSettings.length > 0) {
          data.Bot.apiSettings.forEach(function (element: any) {
            apiListObject[element[0]] = element[1];
          });
        }
        commit("SET_APIS", apiListObject);
        return apiListObject;
      })
      .catch((err) => {
        throw err;
      });
  },
  /**
   * Fetches all API mappings from the backend and commits to store.
   *
   * @param {Object} context - Vuex action context
   * @param {Object} context.state - Current module state
   * @param {Function} context.commit - Vuex commit function
   * @returns {Promise<void>}
   * @throws {Error} When GraphQL query fails
   *
   * @example
   * await store.dispatch('FETCH_ALL_MAPPINGS');
   */
  FETCH_ALL_MAPPINGS: ({ state, commit }) => {
    return graph
      .query({
        query: gql`
          query {
            Bot {
              apiMappings
            }
          }
        `,
        fetchPolicy: "network-only",
      })
      .then((payload) => {
        const data: any = _.cloneDeep(payload.data);
        // console.log(apiArray);
        const mappingListObject: any = {};
        if (Array.isArray(data.Bot.apiMappings) && data.Bot.apiMappings.length > 0) {
          data.Bot.apiMappings.forEach(function (element: any) {
            mappingListObject[element[0]] = element[1];
          });
        }
        commit("SET_API_MAPPINGS", mappingListObject);
        commit("LOADED", 1);
      })
      .catch((err) => {
        commit("LOADED", 1);
        throw err;
      });
  },
  /**
   * Fetches a single API configuration by name (placeholder implementation).
   *
   * @param {Object} context - Vuex action context
   * @param {Function} context.commit - Vuex commit function
   * @param {Object} payload - Action payload
   * @param {string} payload.apiName - Name of the API to fetch
   * @returns {void}
   */
  FETCH_API: ({ commit }, { apiName }) => {},
  /**
   * Creates a new API configuration with the given name.
   *
   * @param {Object} context - Vuex action context
   * @param {Function} context.commit - Vuex commit function
   * @param {Object} payload - Action payload
   * @param {string} payload.apiName - Name for the new API
   * @returns {Promise<any>} Created API data
   * @throws {Error} When GraphQL mutation fails
   *
   * @example
   * const api = await store.dispatch('CREATE_API', { apiName: 'my-api' });
   */
  CREATE_API: ({ commit }, { apiName }) => {
    // console.log(`Create API ${apiName}`);
    return graph
      .mutate({
        mutation: gql`
          mutation ($apiName: String!) {
            apiEditorAPI {
              createApi(apiName: $apiName)
            }
          }
        `,
        variables: { apiName },
      })
      .then((response) => {
        console.log("CREATE_API Response: ", response);
        const data = _.get(response, "data.apiEditorAPI.createApi");
        return data;
      })
      .catch((err) => {
        throw err;
      });
  },
  /**
   * Creates a new API mapping with the given name and associated API.
   *
   * @param {Object} context - Vuex action context
   * @param {Function} context.commit - Vuex commit function
   * @param {Object} payload - Action payload
   * @param {string} payload.apiMappingName - Name for the new mapping
   * @param {string} payload.api - Associated API name
   * @returns {Promise<any>} Created mapping data
   * @throws {Error} When GraphQL mutation fails
   *
   * @example
   * const mapping = await store.dispatch('CREATE_API_MAPPING', {
   *   apiMappingName: 'user-mapping',
   *   api: 'user-api'
   * });
   */
  CREATE_API_MAPPING: ({ commit }, { apiMappingName, api }) => {
    console.log(`Create API mapping ${apiMappingName}`);
    return graph
      .mutate({
        mutation: gql`
          mutation ($apiMappingName: String!, $api: String!) {
            apiEditorAPI {
              createApiMapping(apiMappingName: $apiMappingName, api: $api)
            }
          }
        `,
        variables: { apiMappingName, api },
      })
      .then((response) => {
        console.log("CREATE_API_MAPPING Response: ", response);
        const data = _.get(response, "data.apiEditorAPI.createApiMapping");
        commit("CREATE_API_MAPPING", data);
        return data;
      })
      .catch((err) => {
        throw err;
      });
  },
  /**
   * Saves an API configuration to the backend.
   *
   * @param {Object} context - Vuex action context
   * @param {Function} context.commit - Vuex commit function
   * @param {Object} payload - Action payload
   * @param {string} payload.apiName - Name of the API to save
   * @param {Object} payload.apiObject - API configuration object
   * @returns {Promise<any>} Save response data
   * @throws {Error} When GraphQL mutation fails
   *
   * @example
   * await store.dispatch('SAVE_API', {
   *   apiName: 'my-api',
   *   apiObject: { url: 'https://api.example.com', method: 'GET' }
   * });
   */
  SAVE_API: ({ commit }, { apiName, apiObject }) => {
    return graph
      .mutate({
        mutation: gql`
          mutation ($apiName: String!, $apiObject: JSON!) {
            apiEditorAPI {
              saveApi(apiName: $apiName, apiObject: $apiObject)
            }
          }
        `,
        variables: { apiName, apiObject },
      })
      .then((response) => {
        console.log("SAVE_API Response: ", response);
        const data = _.get(response, "data.apiEditorAPI.saveApi");
        return data;
      })
      .catch((err) => {
        throw err;
      });
  },
  /**
   * Saves an API mapping configuration to the backend.
   *
   * @param {Object} context - Vuex action context
   * @param {Function} context.commit - Vuex commit function
   * @param {Object} payload - Action payload
   * @param {string} payload.apiMappingName - Name of the mapping to save
   * @param {Object} payload.mappingObject - Mapping configuration object
   * @returns {Promise<any>} Save response data
   * @throws {Error} When GraphQL mutation fails
   *
   * @example
   * await store.dispatch('SAVE_API_MAPPING', {
   *   apiMappingName: 'user-mapping',
   *   mappingObject: { api: 'user-api', fields: [...] }
   * });
   */
  SAVE_API_MAPPING: ({ commit }, { apiMappingName, mappingObject }) => {
    return graph
      .mutate({
        mutation: gql`
          mutation ($apiMappingName: String!, $mappingObject: JSON!) {
            apiEditorAPI {
              saveApiMapping(apiMappingName: $apiMappingName, mappingObject: $mappingObject)
            }
          }
        `,
        variables: { apiMappingName, mappingObject },
      })
      .then((response) => {
        console.log("SAVE_API_MAPPING Response: ", response);
        const data = _.get(response, "data.apiEditorAPI.saveApiMapping");
        return data;
      })
      .catch((err) => {
        throw err;
      });
  },
  /**
   * Deletes an API configuration from the backend and removes from store.
   *
   * @param {Object} context - Vuex action context
   * @param {Object} context.state - Current module state
   * @param {Function} context.commit - Vuex commit function
   * @param {Object} payload - Action payload
   * @param {string} payload.apiName - Name of the API to delete
   * @returns {Promise<any>} Deletion response data
   * @throws {Error} When GraphQL mutation fails
   *
   * @example
   * await store.dispatch('DELETE_API', { apiName: 'my-api' });
   */
  DELETE_API: ({ state, commit }, { apiName }) => {
    return graph
      .mutate({
        mutation: gql`
          mutation ($apiName: String!) {
            apiEditorAPI {
              deleteApi(apiName: $apiName)
            }
          }
        `,
        variables: { apiName },
      })
      .then((response) => {
        console.log("DELETE_API Response: ", response);
        commit("DELETE_API", apiName);
        const data = _.get(response, "data.apiEditorAPI.deleteApi");
        return data;
      })
      .catch((err) => {
        throw err;
      });
  },
  /**
   * Deletes an API mapping from the backend and removes from store.
   *
   * @param {Object} context - Vuex action context
   * @param {Function} context.commit - Vuex commit function
   * @param {Object} payload - Action payload
   * @param {string} payload.apiMappingName - Name of the mapping to delete
   * @returns {Promise<any>} Deletion response data
   * @throws {Error} When GraphQL mutation fails
   *
   * @example
   * await store.dispatch('DELETE_API_MAPPING', { apiMappingName: 'user-mapping' });
   */
  DELETE_API_MAPPING: ({ commit }, { apiMappingName }) => {
    return graph
      .mutate({
        mutation: gql`
          mutation ($apiMappingName: String!) {
            apiEditorAPI {
              deleteApiMapping(apiMappingName: $apiMappingName)
            }
          }
        `,
        variables: { apiMappingName },
      })
      .then((response) => {
        console.log("DELETE_API_MAPPING Response: ", response);
        commit("DELETE_API_MAPPING", apiMappingName);
        const data = _.get(response, "data.apiEditorAPI.deleteApiMapping");
        return data;
      })
      .catch((err) => {
        throw err;
      });
  },
  /**
   * Previews API data by executing the API with test data.
   *
   * @param {Object} context - Vuex action context
   * @param {Function} context.commit - Vuex commit function
   * @param {Object} payload - Action payload
   * @param {string} payload.apiName - Name of the API to preview
   * @param {string} payload.apiUrl - API endpoint URL
   * @param {string} payload.body - Request body template
   * @param {Object} payload.testData - Test data for preview
   * @returns {Promise<any>} Preview response data
   * @throws {Error} When GraphQL mutation fails
   *
   * @example
   * const result = await store.dispatch('PREVIEW_API_DATA', {
   *   apiName: 'user-api',
   *   apiUrl: 'https://api.example.com/users',
   *   body: '{"id": "{{userId}}"}',
   *   testData: { userId: '123' }
   * });
   */
  PREVIEW_API_DATA: ({ commit }, { apiName, apiUrl, body, testData }) => {
    return graph
      .mutate({
        mutation: gql`
          mutation ($apiName: String!, $apiUrl: String!, $body: String!, $testData: JSON!) {
            apiEditorAPI {
              previewApiData(apiName: $apiName, apiUrl: $apiUrl, body: $body, testData: $testData)
            }
          }
        `,
        variables: { apiName, apiUrl, body, testData },
      })
      .then((response) => {
        // console.log("PREVIEW_API_DATA Response: ", response);
        const data = _.get(response, "data.apiEditorAPI.previewApiData");
        return data;
      })
      .catch((err) => {
        throw err;
      });
  },
};
export default actions;
